import { useCallback, useState } from 'react'
import { useRecoilValue, useSetRecoilState } from 'recoil'
import { useTranslation } from 'react-i18next'
import type { EditorTextBlockInput } from '@messenger/lotus/row-store'
import { themeOverrideAtom } from '@/atoms'
import { serviceInformationTemplatesAtom } from '@/atoms/_editorAtoms'
import {
  createServiceInformationTemplate,
  getServiceInformationTemplates,
} from '@/api/serviceInformationTemplates'
import { useSetToast } from '@/hooks'
import { useErrorHandler } from '@/hooks/utility/useErrorHandler'
import { editorServiceTemplateContentFromBlocks } from '@/utils/editor-text-box/editorTextBoxAdapter'

export const useEditorServiceTemplateCreate = () => {
  const { t } = useTranslation()
  const [templateName, setTemplateName] = useState('')
  const [showModal, setShowModal] = useState(false)
  const [isCreating, setIsCreating] = useState(false)

  const themeOverride = useRecoilValue(themeOverrideAtom) as {
    product_id: string
  }
  const setServiceInformationTemplates = useSetRecoilState(
    serviceInformationTemplatesAtom,
  )
  const setToast = useSetToast()
  const handleError = useErrorHandler('useEditorServiceTemplateCreate')

  const createTemplateFromBlocks = useCallback(
    async (blocks: EditorTextBlockInput[]) => {
      if (!templateName.trim()) {
        setToast({
          text: t('editor.serviceTemplates.notifications.enterName'),
          icon: 'error',
        })
        return
      }
      if (blocks.length === 0) {
        setToast({
          text: t('editor.serviceTemplates.notifications.enterContent'),
          icon: 'error',
        })
        return
      }

      setIsCreating(true)
      try {
        const productId = themeOverride?.product_id
        if (!productId) {
          throw new Error(
            t('editor.serviceTemplates.notifications.productIdNotFound'),
          )
        }

        await createServiceInformationTemplate({
          name: templateName,
          productId,
          template: editorServiceTemplateContentFromBlocks(blocks),
          category: 'DEFAULT',
        })

        const serviceInformationTemplatesData =
          await getServiceInformationTemplates(productId)
        setServiceInformationTemplates(serviceInformationTemplatesData.data)

        setToast({
          text: t('editor.serviceTemplates.notifications.templateCreated'),
          icon: 'success',
        })
        setShowModal(false)
        setTemplateName('')
      } catch (error) {
        const err = error as {
          response?: { data?: { message?: string } }
          message?: string
        }
        handleError(
          err?.response?.data?.message ||
            err?.message ||
            t('editor.serviceTemplates.notifications.failedToCreateTemplate'),
          error as Error,
        )
      } finally {
        setIsCreating(false)
      }
    },
    [
      templateName,
      themeOverride,
      setToast,
      handleError,
      setServiceInformationTemplates,
      t,
    ],
  )

  return {
    templateName,
    setTemplateName,
    showModal,
    setShowModal,
    isCreating,
    createTemplateFromBlocks,
  }
}

export default useEditorServiceTemplateCreate
